import { createSelector } from 'reselect'
import { excludeNullOrUndefined } from 'checkout/utils'
import {
  CHECKOUT_ITEM_TYPE_BEDBANK,
  CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION,
  CHECKOUT_ITEM_TYPE_CAR_HIRE,
  CHECKOUT_ITEM_TYPE_EXPERIENCE,
  CHECKOUT_ITEM_TYPE_FLIGHT,
  CHECKOUT_ITEM_TYPE_INSURANCE,
  CHECKOUT_ITEM_TYPE_LE_HOTEL
} from 'constants/checkout'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import { RESERVATION_TYPE_INSTANT_BOOKING } from 'constants/reservation'
import { getInsuranceItems } from 'checkout/selectors/view/insurance'
import { getCheckoutInsuranceTravellers } from 'checkout/selectors/request/insurance'
import { getCarHireItems } from '../view/carHire'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { checkoutAccommodationOfferView, getAccommodationItems } from '../view/accommodation'
import { getBundleAndSaveItems } from '../view/bundleAndSave'
import { getFlightItems } from '../view/flights'
import { PROVIDERS } from 'constants/experience'
import { getVillaItems } from '../view/villa'
import { isBookingProtectionSelected } from '../view/bookingProtection'
import { getBookingProtectionOccupantsFromCartItems } from './bookingProtection'
import { selectSelectedTravellerEmployees } from 'businessTraveller/selectors/businessTravellerEmployeeSelectors'
import { getAvailableAccommodationBenefits } from 'luxPlus/selectors/benefits/accommodation'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { finaliseInsuranceItems } from 'checkout/lib/utils/travellerSchema/insuranceTravellerSchema'
import getObjectKey from 'lib/object/getObjectKey'
import getExperienceItemsView from 'checkout/selectors/view/getExperienceItemsView'
import getTransferItemsView from 'checkout/selectors/view/getTransferItemsView'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

function transformOccupantsData(occupancy: App.Occupants, relatedItem?: App.Checkout.TravellerFormSchemaOccupant['relatedItem']) {
  const result: Array<App.Checkout.TravellerFormSchemaOccupant> = []

  if (!occupancy) {
    return result
  }

  for (let i = 0; i < occupancy.adults; i++) {
    result.push({
      type: 'adult',
      relatedItem,
    })
  }
  for (let i = 0; i < (occupancy.children || 0); i++) {
    const childOccupant: App.Checkout.TravellerFormSchemaOccupant = {
      type: 'child',
      relatedItem,
    }
    if (occupancy.childrenAge?.length) {
      childOccupant.age = occupancy.childrenAge[i]
    }
    if (occupancy.childrenBirthDate?.length) {
      childOccupant.birthDate = occupancy.childrenBirthDate[i]
    }
    result.push(childOccupant)
  }
  for (let i = 0; i < (occupancy.infants || 0); i++) {
    const infantOccupant: App.Checkout.TravellerFormSchemaOccupant = {
      type: 'infant',
      relatedItem,
    }

    if (occupancy.childrenBirthDate?.length) {
      infantOccupant.birthDate = occupancy.childrenBirthDate[i]
    }

    result.push(infantOccupant)
  }
  return result
}

function passengersToTravellers(passengers: Array<App.Checkout.FlightPassenger>): Array<App.Checkout.SchemaOccupant> {
  return passengers.map(passenger => {
    return {
      type: passenger.type,
    }
  })
}

const getExperiencesSchemaRequestItems = createSelector(
  (state: App.State) => getExperienceItemsView(state),
  (experienceItemsView): Array<App.Checkout.ExperienceSchemaRequestItem> => {
    const items = experienceItemsView.data
      .filter(excludeNullOrUndefined)
      .filter(item => item.type !== 'addon' && !item.isSessionPurchaseLimitReached)
      .map<App.Checkout.ExperienceSchemaRequestItem | null>(item => {
        const availableTickets = item.ticketSummaryViews.filter(ticket => !ticket.unavailable)
        if (availableTickets.length === 0) {
          return null
        }
        const providerKey = item.experienceId.slice(0, 3)

        const tickets = availableTickets.flatMap(ticket => {
          return Array.from<App.Checkout.ExperienceTicketSchemaRequestItem>({ length: ticket.count }).fill({
            ticketId: ticket.ticketId,
            name: ticket.name,
            fareType: ticket.fareType,
          })
        })

        return {
          type: CHECKOUT_ITEM_TYPE_EXPERIENCE,
          offerId: item.experienceId,
          tickets,
          provider: PROVIDERS[providerKey],
        }
      }).filter(excludeNullOrUndefined)
    return items
  },
)

const getCarHireSchemaRequestItems = createSelector(
  (state: App.State) => getCarHireItems(state),
  (carHireItems): Array<App.Checkout.CarHireSchemaRequestItem> => {
    return carHireItems.map<App.Checkout.CarHireSchemaRequestItem>(carHireItem => ({
      type: CHECKOUT_ITEM_TYPE_CAR_HIRE,
      offerId: carHireItem.offerId,
      dobReference: carHireItem.pickUpDate,
    }))
  },
)

const getTransferSchemaRequestItems = createSelector(
  (state: App.State) => getTransferItemsView(state),
  (transferViews): Array<App.Checkout.ExperienceSchemaRequestItem> => {
    const items = transferViews.data
      .filter(excludeNullOrUndefined)
      .map<App.Checkout.ExperienceSchemaRequestItem | undefined>(itemView => {
        if (!itemView.item.transfer.option) {
          return undefined
        }

        const providerKey = itemView.item.experienceId.slice(0, 3)

        return {
          type: CHECKOUT_ITEM_TYPE_EXPERIENCE,
          offerId: itemView.item.experienceId,
          tickets: [{
            ticketId: itemView.item.transfer.option.id,
            name: itemView.item.transfer.option.name,
            fareType: itemView.item.transfer.option.name,
          }],
          provider: PROVIDERS[providerKey],
        }
      })
      .filter(excludeNullOrUndefined)
    return items
  },
)

const getFlightSchemaRequestItems = createSelector(
  (state: App.State) => getFlightItems(state),
  (flightItems) => {
    return flightItems.flatMap<App.Checkout.FlightSchemaRequestItem>(item => item.flights.map(flight => ({
      type: CHECKOUT_ITEM_TYPE_FLIGHT,
      searchId: item.searchId,
      journeyId: flight.journeyId,
      selectedUpsellOptionId: flight.fareFamily?.id,
      occupants: passengersToTravellers(item.passengers),
    })))
  },
)

const getBundleAndSaveSchemaRequestItems = createSelector(
  (state: App.State) => getBundleAndSaveItems(state),
  (items): Array<App.Checkout.AccommodationSchemaRequestItem> => {
    return items.map<App.Checkout.AccommodationSchemaRequestItem>((item) => {
      return {
        type: 'hotel',
        reservationType: RESERVATION_TYPE_INSTANT_BOOKING,
        occupants: transformOccupantsData(item.occupancy),
        offerId: item.offerId,
      }
    })
  },
)

const getAccommodationSchemaRequestItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.lebt_hotel_loyalty],
  (
    accommodationItems,
    accommodationOfferViews,
    isHotelLoyaltyEnabled,
  ): Array<App.Checkout.AccommodationSchemaRequestItem> => {
    const items = accommodationItems.map<App.Checkout.AccommodationSchemaRequestItem | null>((item) => {
      const baseMetadata = { offerId: item.offerId }

      switch (item.itemType) {
        case CHECKOUT_ITEM_TYPE_BEDBANK:
          const offerView = accommodationOfferViews.data.find(view => view.offerId === item.offerId)
          const itemView = offerView?.itemViews.find(itemView => itemView.item.itemId === item.itemId)
          const bedbankItemView = itemView?.kind === 'bedbank' ? itemView : undefined
          const isEligibleForSupplierLoyaltyProgram = bedbankItemView?.isEligibleForHotelLoyaltyProgram ?? false

          return {
            ...baseMetadata,
            type: OFFER_TYPE_BED_BANK,
            occupants: transformOccupantsData(item.occupancy),
            isEligibleForSupplierLoyaltyProgram: isEligibleForSupplierLoyaltyProgram && (isHotelLoyaltyEnabled || false),
          } as App.Checkout.AccommodationSchemaRequestItem
        case CHECKOUT_ITEM_TYPE_LE_HOTEL:
          return {
            ...baseMetadata,
            type: OFFER_TYPE_HOTEL,
            reservationType: item.reservationType,
            occupants: item.reservationType == RESERVATION_TYPE_INSTANT_BOOKING ? transformOccupantsData(item.occupancy) : undefined,
          } as App.Checkout.AccommodationSchemaRequestItem
        default:
          return null
      }
    }).filter(excludeNullOrUndefined)

    return [
      ...items,
    ]
  },
)

const getVillasSchemaRequestItems = createSelector(
  (state: App.State) => getVillaItems(state),
  (items) => {
    return items.map<App.Checkout.AccommodationSchemaRequestItem>(item => ({
      offerId: item.offerId,
      type: OFFER_TYPE_HOTEL,
      reservationType: item.reservationType,
      occupants: transformOccupantsData(item.occupancy),
    }))
  },
)

const getInsuranceSchemaRequestItems = createSelector(
  (state: App.State) => getInsuranceItems(state),
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (insuranceItems, travellers): Array<App.Checkout.InsuranceTravellerFormSchema> => {
    const item = insuranceItems[0]
    if (!item || !travellers || !item.productId) {
      return []
    }

    return [{
      productId: item.productId,
      quoteId: 'deprecated',
      type: CHECKOUT_ITEM_TYPE_INSURANCE,
      travellers: transformOccupantsData(travellers),
    }]
  },
)

const getBookingProtectionSchemaRequestItems = createSelector(
  (state: App.State) => isBookingProtectionSelected(state),
  (state: App.State) => getBookingProtectionOccupantsFromCartItems(state),
  (hasBookingSelected, occupants): Array<App.Checkout.BookingProtectionSchemaRequestItem> => {
    if (!hasBookingSelected) return []

    if (occupants) {
      return [{
        type: CHECKOUT_ITEM_TYPE_BOOKING_PROTECTION,
        travellers: transformOccupantsData(occupants),
      }]
    } else {
      return []
    }
  },
)

export const isTravellerFormNeeded = createSelector(
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (
    postPurchase,
  ) => {
    return (!postPurchase || postPurchase === 'insurance' || postPurchase === 'select-date')
  },
)

export const getIsLuxPlusMemberRedeemingBenefitSchemaRequest = createSelector(
  (state: App.State) => getCanRedeemLuxPlusBenefits(state),
  (state: App.State) => getAvailableAccommodationBenefits(state),
  (state: App.State) => state.checkout.cart.isGift,
  (canRedeemLuxPlusBenefits, accomBenefits, isGift): boolean => {
    const hasBenefitsWithEditablePrimaryTravellerDetails = accomBenefits.isBundledWithFlights || accomBenefits.isTourWithMemberPrice
    const hasBenefitsWithLockedPrimaryTravellerDetails = (accomBenefits.isMemberOnly ||
      accomBenefits.hasEarlyAccess ||
      accomBenefits.hasLuxPlusInclusions ||
      (accomBenefits.totalMemberPriceSavings ?? 0) > 0)

    return canRedeemLuxPlusBenefits &&
      !isGift &&
      !hasBenefitsWithEditablePrimaryTravellerDetails &&
      hasBenefitsWithLockedPrimaryTravellerDetails
  })

export const getTravellerFormSchemaRequest = createSelector(
  (state: App.State) => getCarHireSchemaRequestItems(state),
  (state: App.State) => getAccommodationSchemaRequestItems(state),
  (state: App.State) => getBundleAndSaveSchemaRequestItems(state),
  (state: App.State) => getExperiencesSchemaRequestItems(state),
  (state: App.State) => getTransferSchemaRequestItems(state),
  (state: App.State) => getFlightSchemaRequestItems(state),
  (state: App.State) => getInsuranceSchemaRequestItems(state),
  (state: App.State) => getBookingProtectionSchemaRequestItems(state),
  (state: App.State) => getVillasSchemaRequestItems(state),
  (state: App.State) => selectSelectedTravellerEmployees(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => state.auth.account.memberId,
  (state: App.State) => state.checkout.cart.isGift,
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => getIsLuxPlusMemberRedeemingBenefitSchemaRequest(state),
  (
    carHireItems,
    accommodationItems,
    bundleAndSaveItems,
    experienceItems,
    transferItems,
    flightItems,
    insuranceItems,
    bookingProtectionItems,
    villaItems,
    selectedTravellerEmployees,
    region,
    customer_id,
    isGift,
    currencyCode,
    isLuxPlusMemberRedeemingBenefitSchemaRequest,
  ) => {
    // Use traveller customerId if activated; else booking manager (unactivated employees have no member account).
    const selectedEmployeeCustomerId = selectedTravellerEmployees[0]?.customerId ?? customer_id
    return {
      items: [
        ...carHireItems,
        ...accommodationItems,
        ...bundleAndSaveItems,
        ...experienceItems,
        ...transferItems,
        ...flightItems,
        ...finaliseInsuranceItems(insuranceItems, flightItems),
        ...bookingProtectionItems,
        ...villaItems,
      ],
      region,
      customer_id: selectedEmployeeCustomerId,
      isGift,
      currencyCode,
      isLuxPlusMember: isLuxPlusMemberRedeemingBenefitSchemaRequest,
    }
  },
)

export const getTravellerFormSchemaRequestKey = createSelector(
  (state: App.State) => getTravellerFormSchemaRequest(state),
  (request) => {
    return getObjectKey(request)
  },
)
